import * as actionConstants from "../constants/actionTypes";
import * as generalHelper from "../helpers/generalHelper";
import * as modalTypes from "../constants/modalTypes";
import * as navigationTypes from "../constants/navigationTypes";
import * as objectTypes from "../constants/objectTypes";
import * as propertyConstants from "../constants/propertyConstants";
import * as reducerHelper from "../helpers/reducerHelper";
import * as tableConstants from "../constants/tableConstants";

import { DuplicateProduct, updateDuplicateProduct } from "../types/duplication/duplicateProduct";
import { Product, updateProduct } from "../types/product";

export type ProductState = Readonly<{
    masterList: Array<Product>;
    masterActiveList: Array<Product>;
    masterCountTotal: number | null;
    masterSearch: string | null;
    masterSearchParams: Record<propertyConstants.Property, string>;
    masterOffset: number;
    masterPage: number;
    masterSystemList: Array<Product>;
    masterSystemActiveList: Array<Product>;
    masterSystemSearch: string | null;
    masterSystemSearchParams: Record<propertyConstants.Property, string>;
    masterSystemOffset: number;
    masterSystemPage: number;
    systemMasterList: Array<Product>;
    systemMasterActiveList: Array<Product>;
    systemMasterSortingAsc: boolean;
    systemMasterSortingCriterion: propertyConstants.Property;
    systemMasterSearch: string | null;
    systemMasterSearchParams: Record<propertyConstants.Property, string>;
    systemMasterOffset: number;
    systemMasterPage: number;
    systemList: Array<Product>;
    systemActiveList: Array<Product>;
    systemCountAvailable: number | null;
    systemCountTotal: number | null;
    systemSortingAsc: boolean;
    systemSortingCriterion: propertyConstants.Property;
    systemSearch: string | null;
    systemSearchParams: Record<propertyConstants.Property, string>;
    systemOffset: number;
    systemPage: number;
    systemZoneList: Array<Product>;
    systemZoneActiveList: Array<Product>;
    systemZoneSearch: string | null;
    systemZoneSearchParams: Record<propertyConstants.Property, string>;
    systemZoneOffset: number;
    systemZonePage: number;
    zoneSystemList: Array<Product>;
    zoneSystemActiveList: Array<Product>;
    zoneSystemSortingAsc: boolean;
    zoneSystemSortingCriterion: propertyConstants.Property;
    zoneSystemSearch: string | null;
    zoneSystemSearchParams: Record<propertyConstants.Property, string>;
    zoneSystemOffset: number;
    zoneSystemPage: number;
    zoneList: Array<Product>;
    zoneActiveList: Array<Product>;
    zoneCountAvailable: number | null;
    zoneCountTotal: number | null;
    zoneSearch: string | null;
    zoneSearchParams: Record<propertyConstants.Property, string>;
    zoneOffset: number;
    zonePage: number;
    duplicateAllList: Array<DuplicateProduct>;
}>;

const initialState: ProductState = {
    masterList: [],
    masterActiveList: [],
    masterCountTotal: null,
    masterSearch: null,
    masterSearchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    masterOffset: tableConstants.DEFAULT_OFFSET,
    masterPage: tableConstants.DEFAULT_PAGE,
    masterSystemList: [],
    masterSystemActiveList: [],
    masterSystemSearch: null,
    masterSystemSearchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    masterSystemOffset: tableConstants.DEFAULT_OFFSET,
    masterSystemPage: tableConstants.DEFAULT_PAGE,
    systemMasterList: [],
    systemMasterActiveList: [],
    systemMasterSortingAsc: tableConstants.DEFAULT_SORTING_ASC,
    systemMasterSortingCriterion: tableConstants.DEFAULT_SORTING_COLUMN,
    systemMasterSearch: null,
    systemMasterSearchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    systemMasterOffset: tableConstants.DEFAULT_OFFSET,
    systemMasterPage: tableConstants.DEFAULT_PAGE,
    systemList: [],
    systemActiveList: [],
    systemCountAvailable: null,
    systemCountTotal: null,
    systemSortingAsc: tableConstants.DEFAULT_SORTING_ASC,
    systemSortingCriterion: tableConstants.DEFAULT_SORTING_COLUMN,
    systemSearch: null,
    systemSearchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    systemOffset: tableConstants.DEFAULT_OFFSET,
    systemPage: tableConstants.DEFAULT_PAGE,
    systemZoneList: [],
    systemZoneActiveList: [],
    systemZoneSearch: null,
    systemZoneSearchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    systemZoneOffset: tableConstants.DEFAULT_OFFSET,
    systemZonePage: tableConstants.DEFAULT_PAGE,
    zoneSystemList: [],
    zoneSystemActiveList: [],
    zoneSystemSortingAsc: tableConstants.DEFAULT_SORTING_ASC,
    zoneSystemSortingCriterion: tableConstants.DEFAULT_SORTING_COLUMN,
    zoneSystemSearch: null,
    zoneSystemSearchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    zoneSystemOffset: tableConstants.DEFAULT_OFFSET,
    zoneSystemPage: tableConstants.DEFAULT_PAGE,
    zoneList: [],
    zoneActiveList: [],
    zoneCountAvailable: null,
    zoneCountTotal: null,
    zoneSearch: null,
    zoneSearchParams: tableConstants.DEFAULT_SEARCH_PARAMS,
    zoneOffset: tableConstants.DEFAULT_OFFSET,
    zonePage: tableConstants.DEFAULT_PAGE,
    duplicateAllList: []
};

export default function products(state: ProductState = initialState, action: Record<string, any>): ProductState {
    switch (action.type) {
        case actionConstants.LIST_SAVE_NO_PARAMS: {
            switch (action.payload.objectType) {
                case objectTypes.SYSTEM_PRODUCT_DUPLICATE:
                case objectTypes.ZONE_PRODUCT_DUPLICATE: {
                    return {
                        ...state,
                        duplicateAllList: action.payload.list
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.LIST_SAVE: {
            switch (action.payload.objectType) {
                case objectTypes.MASTER_PRODUCT:
                case objectTypes.MASTER_PRODUCT_IN_COLOR: {
                    const newActiveList = generalHelper.updateActiveLists(state.masterActiveList, action.payload.list);
                    return {
                        ...state,
                        masterList: action.payload.list,
                        masterActiveList: newActiveList,
                        masterCountTotal: action.payload.totalCount
                    };
                }

                case objectTypes.MASTER_SYSTEM_PRODUCT:
                case objectTypes.MASTER_SYSTEM_PRODUCT_GROUP_WITH_PRODUCTS:
                case objectTypes.MASTER_SYSTEM_PRODUCT_GROUP: {
                    return {
                        ...state,
                        masterSystemList: action.payload.list
                    };
                }
                case objectTypes.SYSTEM_MASTER_PRODUCT:
                case objectTypes.SYSTEM_MASTER_PRODUCT_GROUP_WITH_PRODUCTS:
                case objectTypes.SYSTEM_MASTER_PRODUCT_GROUP: {
                    return {
                        ...state,
                        systemMasterList: action.payload.list
                    };
                }
                case objectTypes.SYSTEM_PRODUCT:
                case objectTypes.SYSTEM_PRODUCT_IN_COLOR: {
                    const newActiveList = generalHelper.updateActiveLists(state.systemActiveList, action.payload.list);

                    return {
                        ...state,
                        systemList: action.payload.list,
                        systemActiveList: newActiveList,
                        systemCountTotal: action.payload.totalCount,
                        systemCountAvailable: action.payload.availableCount
                    };
                }
                case objectTypes.SYSTEM_ZONE_PRODUCT:
                case objectTypes.SYSTEM_ZONE_PRODUCT_GROUP_WITH_PRODUCTS: {
                    return {
                        ...state,
                        systemZoneList: action.payload.list
                    };
                }
                case objectTypes.ZONE_SYSTEM_PRODUCT:
                case objectTypes.ZONE_SYSTEM_PRODUCT_GROUP_WITH_PRODUCTS: {
                    return {
                        ...state,
                        zoneSystemList: action.payload.list
                    };
                }
                case objectTypes.ZONE_PRODUCT:
                case objectTypes.ZONE_PRODUCT_IN_COLOR: {
                    const newActiveList = generalHelper.updateActiveLists(state.zoneActiveList, action.payload.list);

                    return {
                        ...state,
                        zoneList: action.payload.list,
                        zoneActiveList: newActiveList,
                        zoneCountTotal: action.payload.totalCount,
                        zoneCountAvailable: action.payload.availableCount
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.SET_ACTIVE: {
            switch (action.payload.objectType) {
                case objectTypes.MASTER_COLOR_DATA: {
                    // In Color scene Formula bookmark resetting selected product
                    return {
                        ...state,
                        masterActiveList: []
                    };
                }
                case objectTypes.MASTER_PRODUCT:
                case objectTypes.MASTER_PRODUCT_IN_COLOR: {
                    return {
                        ...state,
                        masterActiveList: action.payload.items
                    };
                }
                case objectTypes.MASTER_SYSTEM_PRODUCT:
                case objectTypes.MASTER_SYSTEM_PRODUCT_GROUP_WITH_PRODUCTS:
                case objectTypes.MASTER_SYSTEM_PRODUCT_GROUP: {
                    return {
                        ...state,
                        masterSystemActiveList: action.payload.items
                    };
                }
                case objectTypes.SYSTEM_MASTER_PRODUCT:
                case objectTypes.SYSTEM_MASTER_PRODUCT_GROUP_WITH_PRODUCTS:
                case objectTypes.SYSTEM_MASTER_PRODUCT_GROUP: {
                    const list = generalHelper.updateLists(state.systemMasterList, action.payload.items);
                    const newActiveList = generalHelper.updateActiveLists(action.payload.items, list);

                    return {
                        ...state,
                        systemMasterList: list,
                        systemMasterActiveList: newActiveList
                    };
                }
                case objectTypes.SYSTEM_COLOR_DATA: {
                    // In Color scene Formula bookmark resetting selected product
                    return {
                        ...state,
                        systemActiveList: []
                    };
                }
                case objectTypes.SYSTEM_PRODUCT:
                case objectTypes.SYSTEM_PRODUCT_IN_COLOR: {
                    return {
                        ...state,
                        systemActiveList: action.payload.items
                    };
                }
                case objectTypes.SYSTEM_ZONE_PRODUCT:
                case objectTypes.SYSTEM_ZONE_PRODUCT_GROUP_WITH_PRODUCTS: {
                    return {
                        ...state,
                        systemZoneActiveList: action.payload.items
                    };
                }
                case objectTypes.ZONE_SYSTEM_PRODUCT:
                case objectTypes.ZONE_SYSTEM_PRODUCT_GROUP_WITH_PRODUCTS: {
                    const list = generalHelper.updateLists(state.zoneSystemList, action.payload.items);
                    const newActiveList = generalHelper.updateActiveLists(action.payload.items, list);

                    return {
                        ...state,
                        zoneSystemList: list,
                        zoneSystemActiveList: newActiveList
                    };
                }
                case objectTypes.ZONE_COLOR_DATA: {
                    // In Color scene Formula bookmark resetting selected product
                    return {
                        ...state,
                        zoneActiveList: []
                    };
                }
                case objectTypes.ZONE_PRODUCT:
                case objectTypes.ZONE_PRODUCT_IN_COLOR: {
                    return {
                        ...state,
                        zoneActiveList: action.payload.items
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.UPDATE_ACTIVE: {
            switch (action.payload.objectType) {
                case objectTypes.MASTER_PRODUCT: {
                    return {
                        ...state,
                        masterActiveList: action.payload.items
                    };
                }
                case objectTypes.SYSTEM_PRODUCT: {
                    return {
                        ...state,
                        systemActiveList: action.payload.items
                    };
                }
                case objectTypes.ZONE_PRODUCT: {
                    return {
                        ...state,
                        zoneActiveList: action.payload.items
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.SET_OFFSET: {
            switch (action.payload.objectType) {
                case objectTypes.MASTER_PRODUCT:
                case objectTypes.MASTER_PRODUCT_IN_COLOR: {
                    return {
                        ...state,
                        masterOffset: action.payload.offset,
                        masterPage: action.payload.page
                    };
                }
                case objectTypes.MASTER_SYSTEM_PRODUCT: {
                    return {
                        ...state,
                        masterSystemOffset: action.payload.offset,
                        masterSystemPage: action.payload.page
                    };
                }
                case objectTypes.SYSTEM_PRODUCT:
                case objectTypes.SYSTEM_PRODUCT_IN_COLOR: {
                    return {
                        ...state,
                        systemOffset: action.payload.offset,
                        systemPage: action.payload.page
                    };
                }
                case objectTypes.SYSTEM_ZONE_PRODUCT: {
                    return {
                        ...state,
                        systemZoneOffset: action.payload.offset,
                        systemZonePage: action.payload.page
                    };
                }
                case objectTypes.ZONE_PRODUCT:
                case objectTypes.ZONE_PRODUCT_IN_COLOR: {
                    return {
                        ...state,
                        zoneOffset: action.payload.offset,
                        zonePage: action.payload.page
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.SET_SEARCH: {
            switch (action.payload.objectType) {
                case objectTypes.MASTER_PRODUCT:
                case objectTypes.MASTER_PRODUCT_IN_COLOR: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.MASTER_SEARCH,
                        tableConstants.MASTER_SEARCH_PARAMS
                    );
                }
                case objectTypes.MASTER_SYSTEM_PRODUCT:
                case objectTypes.MASTER_SYSTEM_PRODUCT_GROUP_WITH_PRODUCTS:
                case objectTypes.MASTER_SYSTEM_PRODUCT_GROUP: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.MASTER_SYSTEM_SEARCH,
                        tableConstants.MASTER_SYSTEM_SEARCH_PARAMS
                    );
                }
                case objectTypes.SYSTEM_MASTER_PRODUCT: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.SYSTEM_MASTER_SEARCH,
                        tableConstants.SYSTEM_MASTER_SEARCH_PARAMS
                    );
                }
                case objectTypes.SYSTEM_PRODUCT:
                case objectTypes.SYSTEM_PRODUCT_IN_COLOR: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.SYSTEM_SEARCH,
                        tableConstants.SYSTEM_SEARCH_PARAMS
                    );
                }
                case objectTypes.SYSTEM_ZONE_PRODUCT:
                case objectTypes.SYSTEM_ZONE_PRODUCT_GROUP_WITH_PRODUCTS: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.SYSTEM_ZONE_SEARCH,
                        tableConstants.SYSTEM_ZONE_SEARCH_PARAMS
                    );
                }
                case objectTypes.ZONE_SYSTEM_PRODUCT: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.ZONE_SYSTEM_SEARCH,
                        tableConstants.ZONE_SYSTEM_SEARCH_PARAMS
                    );
                }
                case objectTypes.ZONE_PRODUCT:
                case objectTypes.ZONE_PRODUCT_IN_COLOR: {
                    return reducerHelper.getSearchState(
                        action.payload,
                        state,
                        tableConstants.ZONE_SEARCH,
                        tableConstants.ZONE_SEARCH_PARAMS
                    );
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.SET_SORTING: {
            switch (action.payload.objectType) {
                case objectTypes.SYSTEM_MASTER_PRODUCT:
                case objectTypes.SYSTEM_MASTER_PRODUCT_GROUP_WITH_PRODUCTS:
                case objectTypes.SYSTEM_MASTER_PRODUCT_GROUP: {
                    return {
                        ...state,
                        systemMasterList: generalHelper.sortItemsByProperty(
                            state.systemMasterList,
                            action.payload.criterion,
                            action.payload.asc
                        ),
                        systemMasterSortingAsc: action.payload.asc,
                        systemMasterSortingCriterion: action.payload.criterion
                    };
                }
                case objectTypes.SYSTEM_PRODUCT: {
                    return {
                        ...state,
                        systemSortingAsc: action.payload.asc,
                        systemSortingCriterion: action.payload.criterion
                    };
                }
                case objectTypes.ZONE_SYSTEM_PRODUCT:
                case objectTypes.ZONE_SYSTEM_PRODUCT_GROUP_WITH_PRODUCTS: {
                    return {
                        ...state,
                        zoneSystemList: generalHelper.sortItemsByProperty(
                            state.zoneSystemList,
                            action.payload.criterion,
                            action.payload.asc
                        ),
                        zoneSystemSortingAsc: action.payload.asc,
                        zoneSystemSortingCriterion: action.payload.criterion
                    };
                }
                default: {
                    return state;
                }
            }
        }
        case actionConstants.EDIT: {
            if (!action.payload.isModal) {
                switch (action.payload.objectType) {
                    case objectTypes.MASTER_PRODUCT: {
                        const updatedProductList = generalHelper.updateItem(
                            state.masterList,
                            action.payload.params[propertyConstants.PROPERTY_PRODUCT_ID],
                            action.payload.params[propertyConstants.PROPERTY_DATA],
                            updateProduct
                        );

                        if (updatedProductList.length) {
                            return {
                                ...state,
                                masterList: updatedProductList
                            };
                        }

                        return state;
                    }
                    case objectTypes.SYSTEM_PRODUCT: {
                        const updatedProductList = generalHelper.updateItem(
                            state.systemList,
                            action.payload.params[propertyConstants.PROPERTY_PRODUCT_ID],
                            action.payload.params[propertyConstants.PROPERTY_DATA],
                            updateProduct
                        );

                        if (updatedProductList.length) {
                            return {
                                ...state,
                                systemList: updatedProductList
                            };
                        }
                        return state;
                    }
                    case objectTypes.ZONE_PRODUCT: {
                        const updatedProductList = generalHelper.updateItem(
                            state.zoneList,
                            action.payload.params[propertyConstants.PROPERTY_PRODUCT_ID],
                            action.payload.params[propertyConstants.PROPERTY_DATA],
                            updateProduct
                        );

                        if (updatedProductList.length) {
                            return {
                                ...state,
                                zoneList: updatedProductList
                            };
                        }

                        return state;
                    }
                    default: {
                        return state;
                    }
                }
            }
            return state;
        }
        case actionConstants.EDIT_SUCCESS: {
            if (!action.payload.isModal) {
                switch (action.payload.objectType) {
                    case objectTypes.MASTER_PRODUCT: {
                        return {
                            ...state,
                            masterList: generalHelper.updateLists(state.masterList, action.payload.items),
                            masterActiveList: generalHelper.updateActiveLists(
                                state.masterActiveList,
                                action.payload.items
                            )
                        };
                    }
                    case objectTypes.SYSTEM_PRODUCT: {
                        return {
                            ...state,
                            systemList: generalHelper.updateLists(state.systemList, action.payload.items),
                            systemActiveList: generalHelper.updateActiveLists(
                                state.systemActiveList,
                                action.payload.items
                            )
                        };
                    }
                    case objectTypes.ZONE_PRODUCT: {
                        return {
                            ...state,
                            zoneList: generalHelper.updateLists(state.zoneList, action.payload.items),
                            zoneActiveList: generalHelper.updateActiveLists(state.zoneActiveList, action.payload.items)
                        };
                    }
                    default: {
                        return state;
                    }
                }
            }
            return state;
        }
        case actionConstants.UPDATE_ITEM: {
            switch (action.payload.objectType) {
                case objectTypes.SYSTEM_PRODUCT_DUPLICATE:
                case objectTypes.ZONE_PRODUCT_DUPLICATE: {
                    if (action.payload.list.length) {
                        const list = state.duplicateAllList.map((item) => {
                            if (
                                item[propertyConstants.PROPERTY_ID] ===
                                action.payload.list[0][propertyConstants.PROPERTY_ID]
                            ) {
                                const updatedItem = updateDuplicateProduct(
                                    item,
                                    action.payload.list[0][propertyConstants.PROPERTY_DATA]
                                );
                                return updatedItem ? updatedItem : item;
                            }
                            return item;
                        });

                        return {
                            ...state,
                            duplicateAllList: list
                        };
                    }

                    return {
                        ...state
                    };
                }
                default: {
                    return state;
                }
            }
            return state;
        }
        case actionConstants.NAVIGATION_SET_SCENE:
        case actionConstants.NAVIGATION_SET_WIZARD: {
            if (action.payload.wizard === null || action.payload.scene === navigationTypes.SYSTEM_PRODUCTS_SCENE) {
                return {
                    ...initialState,
                    systemActiveList: state.systemActiveList
                };
            }

            return initialState;
        }
        case actionConstants.NAVIGATION_CLOSE_ALL_MODALS: {
            return {
                ...state,
                masterSystemList: initialState.masterSystemList,
                masterSystemActiveList: initialState.masterSystemActiveList,
                masterSystemSearch: initialState.masterSystemSearch,
                masterSystemSearchParams: initialState.masterSystemSearchParams,
                systemMasterList: initialState.systemMasterList,
                systemMasterActiveList: initialState.systemMasterActiveList,
                systemZoneList: initialState.systemZoneList,
                systemZoneActiveList: initialState.systemZoneActiveList,
                systemZoneSearch: initialState.systemZoneSearch,
                systemZoneSearchParams: initialState.systemZoneSearchParams,
                zoneSystemList: initialState.zoneSystemList,
                zoneSystemActiveList: initialState.zoneSystemActiveList
            };
        }
        case actionConstants.NAVIGATION_MODAL_CLOSE: {
            if (
                action.payload.type === modalTypes.MODAL_SYSTEM_PRODUCTS_PROPAGATE ||
                action.payload.type === modalTypes.MODAL_ZONE_PRODUCTS_PROPAGATE
            ) {
                return {
                    ...state,
                    masterSystemList: initialState.masterSystemList,
                    masterSystemActiveList: initialState.masterSystemActiveList,
                    masterSystemSearch: initialState.masterSystemSearch,
                    masterSystemSearchParams: initialState.masterSystemSearchParams,
                    systemMasterList: initialState.systemMasterList,
                    systemMasterActiveList: initialState.systemMasterActiveList,
                    systemZoneList: initialState.systemZoneList,
                    systemZoneActiveList: initialState.systemZoneActiveList,
                    systemZoneSearch: initialState.systemZoneSearch,
                    systemZoneSearchParams: initialState.systemZoneSearchParams,
                    zoneSystemList: initialState.zoneSystemList,
                    zoneSystemActiveList: initialState.zoneSystemActiveList
                };
            }
            return state;
        }
        case actionConstants.EVENT_SAVE_DATABASES: {
            return initialState;
        }
        case actionConstants.EVENT_SAVE_SYSTEMS: {
            return {
                ...initialState,
                masterList: state.masterList,
                masterActiveList: state.masterActiveList,
                masterCountTotal: state.masterCountTotal,
                masterSearch: state.masterSearch,
                masterSearchParams: state.masterSearchParams,
                masterOffset: state.masterOffset,
                masterPage: state.masterPage
            };
        }
        case actionConstants.EVENT_SAVE_ZONES: {
            return {
                ...initialState,
                masterList: state.masterList,
                masterActiveList: state.masterActiveList,
                masterCountTotal: state.masterCountTotal,
                masterSearch: state.masterSearch,
                masterSearchParams: state.masterSearchParams,
                masterOffset: state.masterOffset,
                masterPage: state.masterPage,
                systemList: state.systemList,
                systemActiveList: state.systemActiveList,
                systemCountTotal: state.systemCountTotal,
                systemSortingAsc: state.systemSortingAsc,
                systemSortingCriterion: state.systemSortingCriterion,
                systemSearch: state.systemSearch,
                systemSearchParams: state.systemSearchParams,
                systemOffset: state.systemOffset,
                systemPage: state.systemPage,
                systemZoneList: state.systemZoneList
            };
        }
        case actionConstants.DATA_ROLLBACK: {
            return initialState;
        }
        case actionConstants.SERVER_WEBSOCKET_CLOSED: {
            return initialState;
        }
        default: {
            return state;
        }
    }
}
